import { create } from 'zustand';
import type { ProjectEvent } from '../types';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';

export interface SharedProjectEvent extends ProjectEvent { actorId: string | null }
export interface ActivityRow {
  id: string; project_id: string; actor_id: string | null; at: string; kind: ProjectEvent['kind'];
  item_id: string | null; title: string | null; from_value: string | null; to_value: string | null;
}
interface ActivityState {
  rows: Record<string, SharedProjectEvent[]>;
  actorNames: Record<string, string>;
  loading: Record<string, boolean>;
  readErrors: Record<string, string | undefined>;
  writeErrors: Record<string, string | undefined>;
  pending: ActivityRow[];
  storageError: string | null;
}
const KEY = 'metanoia:project-activity-outbox:v1';
function loadPending(): ActivityRow[] {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(KEY) ?? '[]');
    return Array.isArray(value) ? value.filter((row): row is ActivityRow => !!row && typeof row === 'object'
      && typeof row.id === 'string' && typeof row.project_id === 'string' && typeof row.actor_id === 'string'
      && typeof row.at === 'string' && typeof row.kind === 'string') : [];
  } catch { return []; }
}
export const useProjectActivityRemote = create<ActivityState>(() => ({
  rows: {}, actorNames: {}, loading: {}, readErrors: {}, writeErrors: {}, pending: loadPending(), storageError: null,
}));
function persistPending() {
  try {
    localStorage.setItem(KEY, JSON.stringify(useProjectActivityRemote.getState().pending));
    useProjectActivityRemote.setState({ storageError: null });
  } catch {
    useProjectActivityRemote.setState({ storageError: 'History is only queued in this tab. Keep it open until sync succeeds.' });
  }
}
function message(error: unknown): string {
  return error && typeof error === 'object' && 'message' in error ? String(error.message) : 'Shared history could not sync.';
}
export function activityRow(projectId: string, actorId: string, event: ProjectEvent): ActivityRow {
  return { id: event.id, project_id: projectId, actor_id: actorId, at: event.at, kind: event.kind,
    item_id: event.itemId ?? null, title: event.title ?? null, from_value: event.from ?? null, to_value: event.to ?? null };
}
/**
 * ⚠️ Ids the server has CONFIRMED storing, per project (review 005-R5a).
 *
 * An owner's edit to an already-shared project passes the whole retained blob
 * history along with the new event, so that history reaches the table on the first
 * edit. Without this record it was re-sent on EVERY edit: up to 200 rows a time,
 * and once the table passed its 200-row cap, the pruned old rows were inserted and
 * pruned again on each edit, firing a realtime INSERT at every open viewer per row.
 *
 * Ids are recorded only after the batch containing them SUCCEEDS, so a failed copy
 * is never mistaken for a finished one. Anything not yet confirmed is still sent —
 * which also catches an event the blob received before `sharedMeta` had loaded.
 * If storage is lost, the next edit re-sends once; the insert is DO NOTHING, so that
 * costs a request, not a duplicate.
 */
const CONFIRMED_KEY = 'metanoia:project-activity-confirmed:v1';
const CONFIRMED_PER_PROJECT = 400;
let confirmedFallback: Record<string, string[]> = {};
function readConfirmed(): Record<string, string[]> {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(CONFIRMED_KEY) ?? '{}');
    return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, string[]> : {};
  } catch { return confirmedFallback; }
}
function confirmStored(projectId: string, ids: string[]): void {
  const all = readConfirmed();
  const next = { ...all, [projectId]: [...new Set([...(all[projectId] ?? []), ...ids])].slice(-CONFIRMED_PER_PROJECT) };
  confirmedFallback = next;
  try { localStorage.setItem(CONFIRMED_KEY, JSON.stringify(next)); } catch { /* kept in memory for this tab */ }
}

/** Called only by an explicit edit/share. Original UUIDs make every retry idempotent. */
export function queueProjectActivity(projectId: string, actorId: string, events: ProjectEvent[]): void {
  const confirmed = new Set(readConfirmed()[projectId] ?? []);
  events = events.filter(event => !confirmed.has(event.id));
  if (!events.length) return;
  const pending = new Map(useProjectActivityRemote.getState().pending.map(row => [row.id, row]));
  for (const event of events) if (!pending.has(event.id)) pending.set(event.id, activityRow(projectId, actorId, event));
  useProjectActivityRemote.setState({ pending: [...pending.values()].sort((a, b) => Date.parse(a.at) - Date.parse(b.at)) });
  persistPending();
  void syncProjectActivity();
}
const clearedGeneration = new Map<string, number>();
let syncing: Promise<void> | null = null;
/** One cycle retries only previously authored rows; never diffs fetched snapshots. */
export function syncProjectActivity(): Promise<void> {
  if (syncing) return syncing;
  syncing = (async () => {
    if (!useProjectActivityRemote.getState().pending.length) return;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return;
      const pending = useProjectActivityRemote.getState().pending.filter(row => row.actor_id === user.id);
      for (const projectId of new Set(pending.map(row => row.project_id))) {
        const batch = pending.filter(row => row.project_id === projectId);
        const generation = clearedGeneration.get(projectId);
        try {
          // DO NOTHING on conflict: append-only RLS must never need an UPDATE policy.
          const { error } = await supabase.from('project_activity').upsert(batch, { onConflict: 'id', ignoreDuplicates: true });
          if (error) throw error;
          if (clearedGeneration.get(projectId) !== generation) continue;
          confirmStored(projectId, batch.map(row => row.id));
          const sent = new Set(batch.map(row => row.id));
          useProjectActivityRemote.setState(state => ({
            pending: state.pending.filter(row => !sent.has(row.id)),
            writeErrors: { ...state.writeErrors, [projectId]: undefined },
          }));
          persistPending();
          await fetchProjectActivity(projectId, true);
        } catch (error) {
          useProjectActivityRemote.setState(state => ({ writeErrors: { ...state.writeErrors, [projectId]: message(error) } }));
        }
      }
    } catch (error) {
      useProjectActivityRemote.setState(state => ({ writeErrors: Object.fromEntries(state.pending.map(row => [row.project_id, message(error)])) }));
    }
  })().finally(() => { syncing = null; });
  return syncing;
}
const reads = new Map<string, Promise<void>>();
const refreshAgain = new Set<string>();
export function fetchProjectActivity(projectId: string, afterInsert = false): Promise<void> {
  const current = reads.get(projectId);
  if (current) {
    // An insert may arrive after this request's snapshot. Refetch once it ends.
    if (afterInsert) refreshAgain.add(projectId);
    return current;
  }
  const request = (async () => {
    useProjectActivityRemote.setState(state => ({ loading: { ...state.loading, [projectId]: true } }));
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) throw new Error('Sign in to read shared history.');
      const { data, error } = await supabase.from('project_activity')
        .select('id, project_id, actor_id, at, kind, item_id, title, from_value, to_value')
        .eq('project_id', projectId).order('at', { ascending: false }).order('id', { ascending: false }).limit(50);
      if (error) throw error;
      const rows = (data as ActivityRow[] ?? []).map(row => ({ id: row.id, actorId: row.actor_id, at: row.at, kind: row.kind,
        itemId: row.item_id ?? undefined, title: row.title ?? undefined, from: row.from_value ?? undefined, to: row.to_value ?? undefined }));
      useProjectActivityRemote.setState(state => ({ rows: { ...state.rows, [projectId]: rows }, readErrors: { ...state.readErrors, [projectId]: undefined } }));
      // Owners need not have a project_members row. Resolve every recorded actor,
      // including owners and people who have since left, through the same profile
      // table used by the collaboration roster. A deleted account stays null.
      const actorIds = [...new Set(rows.flatMap(row => row.actorId ? [row.actorId] : []))];
      if (actorIds.length) {
        const { data: profiles, error: profileError } = await supabase.from('user_profiles').select('id, name, handle').in('id', actorIds);
        if (profileError) throw new Error('History entries loaded, but actor names could not load.');
        const actorNames = Object.fromEntries((profiles ?? []).flatMap((profile: { id: string; name: string | null; handle: string | null }) => {
          const name = profile.name ?? profile.handle;
          return name === null ? [] : [[profile.id, name]];
        }));
        useProjectActivityRemote.setState(state => ({ actorNames: { ...state.actorNames, ...actorNames } }));
      }
    } catch (error) {
      useProjectActivityRemote.setState(state => ({ readErrors: { ...state.readErrors, [projectId]: message(error) } }));
    } finally {
      useProjectActivityRemote.setState(state => ({ loading: { ...state.loading, [projectId]: false } }));
    }
  })().finally(() => {
    reads.delete(projectId);
    if (refreshAgain.delete(projectId)) return fetchProjectActivity(projectId);
  });
  reads.set(projectId, request);
  return request;
}
export async function subscribeProjectActivity(projectId: string): Promise<() => void> {
  try {
    const { user } = await syncManager.safeAuthCheck();
    if (!user) return () => {};
    const channel = supabase.channel(`project-activity-${projectId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'project_activity', filter: `project_id=eq.${projectId}` }, () => { void fetchProjectActivity(projectId, true); })
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  } catch (error) {
    useProjectActivityRemote.setState(state => ({ readErrors: { ...state.readErrors, [projectId]: message(error) } }));
    return () => {};
  }
}
// Loading merely restores the outbox. Only a later sync cycle sends authored rows.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { void syncProjectActivity(); });
  window.setInterval(() => { void syncProjectActivity(); }, 30_000);
}

/** Full, fail-closed copy-back read; the ordinary 50-row UI read is insufficient. */
export async function readFullProjectHistory(projectId: string, me: string): Promise<{ events: ProjectEvent[]; names: Record<string, string> }> {
  const pendingBefore = useProjectActivityRemote.getState().pending.filter(row => row.project_id === projectId);
  const all: ActivityRow[] = [];
  for (let offset = 0; offset < 200; offset += 50) {
    const { data, error } = await supabase.from('project_activity').select('id, project_id, actor_id, at, kind, item_id, title, from_value, to_value').eq('project_id', projectId).order('at', { ascending: false }).order('id', { ascending: false }).range(offset, offset + 49);
    if (error) throw error;
    all.push(...(data ?? []) as ActivityRow[]);
    if ((data?.length ?? 0) < 50) break;
  }
  all.push(...pendingBefore, ...useProjectActivityRemote.getState().pending.filter(row => row.project_id === projectId));
  const ids = [...new Set(all.flatMap(row => row.actor_id ? [row.actor_id] : []))];
  const names: Record<string, string> = {};
  if (ids.length) {
    const { data, error } = await supabase.from('user_profiles').select('id, name, handle').in('id', ids);
    if (error) throw error;
    for (const profile of data ?? []) if (profile.name || profile.handle) names[profile.id] = profile.name || profile.handle;
  }
  return { names, events: all.map(row => ({ id: row.id, at: row.at, kind: row.kind, itemId: row.item_id ?? undefined, title: row.title ?? undefined, from: row.from_value ?? undefined, to: row.to_value ?? undefined, actorId: row.actor_id, ...(row.actor_id && row.actor_id !== me && names[row.actor_id] ? { actorName: names[row.actor_id] } : {}) })) };
}
export function clearProjectActivity(projectId: string): void {
  clearedGeneration.set(projectId, (clearedGeneration.get(projectId) ?? 0) + 1);
  useProjectActivityRemote.setState(state => ({ pending: state.pending.filter(row => row.project_id !== projectId), rows: { ...state.rows, [projectId]: [] }, writeErrors: { ...state.writeErrors, [projectId]: undefined } }));
  persistPending();
  const confirmed = { ...readConfirmed() }; delete confirmed[projectId]; confirmedFallback = confirmed;
  try { localStorage.setItem(CONFIRMED_KEY, JSON.stringify(confirmed)); } catch { /* memory remains authoritative for this tab */ }
}
